import type { MouseEvent } from "react";
import { Icon, IconButton, useToast } from "@astrum/ui";
import { formatBytes, iconForType } from "../../components/AttachmentList.js";
import { DocumentViewer, VIEWABLE_DOCUMENT_TYPES } from "../../components/DocumentViewer.js";
import { FilePreview } from "../../components/FilePreview.js";
import { ImageLightbox } from "../../components/ImageLightbox.js";
import { ScanBadge } from "../../components/ScanBadge.js";
import { isPendingScan } from "../../lib/scan-status.js";
import { downloadFile, fileUrl, isDesktop, openFile } from "../../lib/transport.js";
import { INLINE_IMAGE_TYPES } from "./types.js";
import type { MessageAttachment } from "./types.js";

/** The file open in a viewer: an image in the lightbox, or a document in the PDF viewer. */
export type ViewedAttachment = { kind: "image" | "document"; id: string; fileName: string };

/**
 * The files shared with one message. An image opens a lightbox in place;
 * anything else gets a row naming it, because a spreadsheet has nothing to
 * show inline.
 *
 * The desktop webview has no tabs to open a file in, so the href is kept for
 * the non-image row (for web, and for right-click/open-in-new-tab), but the
 * click itself is intercepted on desktop to fetch the file and open it in its
 * default application instead. See openFile in transport.ts.
 */
export function MessageAttachments({
  attachments,
  onView,
}: {
  attachments: MessageAttachment[] | undefined;
  onView: (file: ViewedAttachment) => void;
}) {
  const { toast } = useToast();

  return (
    <>
      {attachments?.map((attachment) => {
        const path = `/api/v1/files/${attachment.id}/download`;
        const onClick = isDesktop
          ? (event: MouseEvent<HTMLAnchorElement>) => {
              event.preventDefault();
              void openFile(path, attachment.fileName).catch(() =>
                toast(`Could not open ${attachment.fileName}.`, { tone: "danger" }),
              );
            }
          : undefined;

        const isImage = INLINE_IMAGE_TYPES.has(attachment.contentType);

        // Still being virus-scanned: shown, but with nothing to open —
        // the file routes refuse it until it is clean, and the thread
        // polls until it flips.
        if (isPendingScan(attachment)) {
          return (
            <div key={attachment.id} className="chat-message__file is-scanning" aria-disabled="true">
              <span className="chat-message__file-main">
                <Icon name={iconForType(attachment.contentType)} size={15} />
                <span className="chat-message__file-name">{attachment.fileName}</span>
                <span className="chat-message__file-size">{formatBytes(attachment.sizeBytes)}</span>
                <ScanBadge file={attachment} />
              </span>
            </div>
          );
        }

        if (isImage) {
          return (
            <button
              key={attachment.id}
              type="button"
              onClick={() => onView({ kind: "image", id: attachment.id, fileName: attachment.fileName })}
              className="chat-message__image"
              title={`${attachment.fileName} — ${formatBytes(attachment.sizeBytes)}`}
            >
              {/* Lazy, because a thread scrolled back through months of
                  screenshots should not fetch all of them at once. */}
              <FilePreview fileId={attachment.id} alt={attachment.fileName} />
            </button>
          );
        }

        /* A document row carries its own controls, so nobody has to go
           to Files to get at something shared in the conversation:
           Preview (PDFs open in the in-app viewer) and Download. The
           name itself does whichever of the two applies. */
        const canView = VIEWABLE_DOCUMENT_TYPES.has(attachment.contentType);
        const view = () => onView({ kind: "document", id: attachment.id, fileName: attachment.fileName });
        const download = () =>
          void downloadFile(path, attachment.fileName).catch(() =>
            toast(`Could not download ${attachment.fileName}.`, { tone: "danger" }),
          );

        return (
          <div key={attachment.id} className="chat-message__file">
            <a
              href={fileUrl(path)}
              className="chat-message__file-main"
              onClick={
                canView
                  ? (event) => {
                      event.preventDefault();
                      view();
                    }
                  : onClick
              }
            >
              <Icon name={iconForType(attachment.contentType)} size={15} />
              <span className="chat-message__file-name">{attachment.fileName}</span>
              <span className="chat-message__file-size">{formatBytes(attachment.sizeBytes)}</span>
            </a>
            <span className="chat-message__file-actions">
              {canView && (
                <IconButton
                  size="sm"
                  icon={<Icon name="eye" size={14} />}
                  label={`Preview ${attachment.fileName}`}
                  onClick={view}
                />
              )}
              <IconButton
                size="sm"
                icon={<Icon name="download" size={14} />}
                label={`Download ${attachment.fileName}`}
                onClick={download}
              />
            </span>
          </div>
        );
      })}
    </>
  );
}

/** The full-size viewers an attachment opens into. Both stay mounted; each
 *  renders nothing unless it is the one in use. */
export function AttachmentViewer({ viewing, onClose }: { viewing: ViewedAttachment | null; onClose: () => void }) {
  return (
    <>
      <ImageLightbox
        open={viewing?.kind === "image"}
        fileId={viewing?.kind === "image" ? viewing.id : ""}
        fileName={viewing?.kind === "image" ? viewing.fileName : ""}
        onClose={onClose}
      />
      <DocumentViewer
        open={viewing?.kind === "document"}
        fileId={viewing?.kind === "document" ? viewing.id : ""}
        fileName={viewing?.kind === "document" ? viewing.fileName : ""}
        onClose={onClose}
      />
    </>
  );
}
